import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import {
  IManagedDbBatchPreviewItem,
  IManagedDbBatchPreviewParameter,
  IManagedDbBatchPreviewResponse,
  IManagedDbBatchRequest,
  TableEditorService
} from "../../services/table-editor-service";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import TableEditorAlert from "./table-editor-alert";
import { buildTableEditorErrorAlert, ITableEditorAlert } from "./table-editor-errors";

// KOA-5857 — "Preview SQL" modal for the pending batch.
//
// Read-only window into what the next Save would execute. Built so the
// SQL text shown here is byte-identical to what BatchMutate runs (server
// uses the same BuildMutationCommand for both paths) — the preview can't
// drift from the executed statements.
//
// The user can either close the modal and tweak their pending changes,
// or commit straight from the preview via the "Save N" affordance — both
// land in the parent's saveAll without any state outside this modal.

export interface ISqlPreviewModalProps {
  isOpen: boolean;
  // Request is null when the modal is closed; non-null when open. Passed
  // by the parent so the preview path uses the same wire-format builder
  // as the actual save (buildBatchRequestFromPending).
  request: IManagedDbBatchRequest | null;
  isSaving: boolean;
  onClose: () => void;
  // Triggered from the in-modal "Save N" button. Parent runs the real
  // save through its existing saveAll; the modal closes after.
  onConfirmSave: () => void;
}

const opBadgeText = (op: string): string => {
  if (op === "insert") {
    return "INSERT";
  }
  if (op === "update") {
    return "UPDATE";
  }
  if (op === "delete") {
    return "DELETE";
  }
  return op.toUpperCase();
};

const SqlPreviewModal: React.FC<ISqlPreviewModalProps> = (props: ISqlPreviewModalProps): JSX.Element | null => {
  const { isOpen, request, isSaving, onClose, onConfirmSave } = props;

  const [items, setItems] = useState<IManagedDbBatchPreviewItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<ITableEditorAlert | null>(null);

  // AbortController per fetch so closing the modal mid-load drops the
  // pending response before it hits setState on an unmounted tree.
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(true);
  useEffect((): (() => void) => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect((): void => {
    if (!isOpen || !request) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setLoadError(null);
    setItems([]);
    (async (): Promise<void> => {
      try {
        const response = await TableEditorService.BatchPreview(request, controller.signal);
        if (controller.signal.aborted || !mountedRef.current) {
          return;
        }
        const payload: IManagedDbBatchPreviewResponse = response.data;
        setItems(payload.items ?? []);
      } catch (error: unknown) {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }
        if (!mountedRef.current) {
          return;
        }
        const alert: ITableEditorAlert = await buildTableEditorErrorAlert(error, {
          operation: "sqlPreview",
          title: "Could not preview SQL",
          fallbackMessage: "The server could not build SQL for the pending changes."
        });
        setLoadError(alert);
      } finally {
        if (mountedRef.current && abortRef.current === controller) {
          setIsLoading(false);
          abortRef.current = null;
        }
      }
    })();
  }, [isOpen, request]);

  if (!isOpen) {
    return null;
  }

  const mutationCount: number = request?.mutations.length ?? 0;
  const errorCount: number = items.filter((i: IManagedDbBatchPreviewItem): boolean => Boolean(i.error)).length;

  const headerPills: JSX.Element[] = [
    <span key="count" className="kuika-table-editor__cell-expander-pill">
      {`${mutationCount} ${mutationCount === 1 ? "statement" : "statements"}`}
    </span>
  ];
  if (errorCount > 0) {
    headerPills.push(
      <span key="failing" className="kuika-table-editor__cell-expander-pill">
        {`${errorCount} failing`}
      </span>
    );
  }

  const confirmSaveAndClose = (): void => {
    onConfirmSave();
    onClose();
  };

  return (
    <KModal
      showModal={isOpen}
      closeModal={onClose}
      header={
        <span className="kuika-table-editor__cell-expander-header">
          <span className="kuika-table-editor__cell-expander-header-kicker" aria-hidden="true">
            Preview SQL
          </span>
          <span className="kuika-table-editor__cell-expander-header-title">Pending save</span>
          <span className="kuika-table-editor__cell-expander-meta-pills">{headerPills}</span>
        </span>
      }
      width={760}
      footer={
        <KModalFooter
          onCancel={onClose}
          cancelLabel="Close"
          cancelTestId="sqlPreviewCloseButton"
          submitLabel={`Save ${mutationCount}`}
          submitTestId="sqlPreviewSaveButton"
          onSubmit={confirmSaveAndClose}
          loading={isSaving}
          disabled={isSaving || isLoading || mutationCount === 0 || errorCount === items.length}
        >
          <span className="kuika-table-editor__sql-preview-help">Statements run in the order shown.</span>
        </KModalFooter>
      }
    >
      <div className="kuika-table-editor__sql-preview">
        {loadError && <TableEditorAlert alert={loadError} onDismiss={(): void => setLoadError(null)} />}

        {isLoading && <div className="kuika-table-editor__sql-preview-loading">Building SQL…</div>}

        {!isLoading && !loadError && items.length === 0 && (
          <div className="kuika-table-editor__sql-preview-empty">No pending mutations to preview.</div>
        )}

        {!isLoading && items.length > 0 && (
          <>
            {errorCount > 0 && (
              <div className="kuika-table-editor__banner kuika-table-editor__banner--warn">
                {errorCount} of {items.length} mutation{items.length === 1 ? "" : "s"} would fail validation before
                reaching the database.
              </div>
            )}
            <ol className="kuika-table-editor__sql-preview-list">
              {items.map((item: IManagedDbBatchPreviewItem, idx: number): JSX.Element => (
                <li key={item.clientId ?? `i-${idx}`} className="kuika-table-editor__sql-preview-item">
                  <div className="kuika-table-editor__sql-preview-header">
                    <span
                      className={`kuika-table-editor__sql-preview-badge kuika-table-editor__sql-preview-badge--${item.op}`}
                    >
                      {opBadgeText(item.op)}
                    </span>
                    {item.error ? (
                      <span className="kuika-table-editor__sql-preview-error">{item.error}</span>
                    ) : (
                      <code className="kuika-table-editor__sql-preview-sql">{item.sql}</code>
                    )}
                  </div>
                  {!item.error && item.parameters.length > 0 && (
                    <ul className="kuika-table-editor__sql-preview-params">
                      {item.parameters.map((p: IManagedDbBatchPreviewParameter): JSX.Element => (
                        <li key={p.name}>
                          <code>{p.name}</code> = <code>{p.value}</code>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </KModal>
  );
};

export default SqlPreviewModal;
